import { Component, effect, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { RadioButtonModule } from 'primeng/radiobutton';
import { UniqueIdService } from '../../../services/builder/unique-id.service';
import { WidgetService } from '../../../services/builder/widget.service';
import { baseCustomPanelStructureComponent } from '../../new-custom-panel/baseCustomPanelStructure';
import { CheckBoxCustomization } from './checkbox-edit';
import { TextareaModule } from 'primeng/textarea';

@Component({
  selector: 'lib-checkbox-custom-panel',
  template: `
    <lib-base-cpanel
      [content]="content"
      [form]="customizationForm"
      header="Personalizzazione Casella di Spunta"
      (closed)="updateWidget($event)"
    />
    <ng-template #content>
      @if (customizationForm) {
        <div [formGroup]="customizationForm" class="flex w-full flex-col gap-2">
          <div class="flex w-full flex-col">
            <label for="desc">Descrizione</label>
            <textarea
              id="desc"
              pTextarea
              formControlName="description"
              class="p-fluid w-full"
            ></textarea>
          </div>
          <div class="flex w-full flex-col">
            <label for="weight">Peso (quando selezionato)</label>
            <small id="weightHelp">
              Il peso determina quanto questo campo incide sul punteggio totale
              della sottosezione.<br />
              Un valore più alto indica maggiore importanza rispetto agli altri
              campi.<br />
              Imposta a 0 se non vuoi che questo campo contribuisca al
              punteggio.
            </small>
            <p-input-number
              [autofocus]="false"
              formControlName="weight"
              fluid
              formControlName="weight"
              ariaDescribedBy="weightHelp"
              mode="decimal"
              minFractionDigits="0"
              maxFractionDigits="2"
            ></p-input-number>
          </div>
          <div class="flex w-full flex-col">
            <label for="desc">Valore predefinito</label>
            <div class="flex w-full flex-row justify-end gap-2">
              <p-radioButton
                [autofocus]="false"
                inputId="rb-default-true"
                [value]="true"
                formControlName="defaultValue"
              />
              <label for="rb-default-true">Vero</label>

              <p-radioButton
                [autofocus]="false"
                inputId="rb-default-false"
                [value]="false"
                formControlName="defaultValue"
              />
              <label for="rb-default-false">Falso</label>
            </div>
          </div>
          <div class="flex w-full flex-row">
            <label for="required" class="w-full self-start">
              Campo obbligatorio
            </label>
            <p-checkbox
              id="required"
              formControlName="required"
              [binary]="true"
              class="self-end"
            ></p-checkbox>
          </div>
        </div>
      }
    </ng-template>
  `,
  imports: [
    ReactiveFormsModule,
    baseCustomPanelStructureComponent,
    InputTextModule,
    InputNumberModule,
    MatButtonModule,
    RadioButtonModule,
    CheckboxModule,
    TextareaModule,
  ],
})
export class CheckBoxCPanelComponent {
  private fb = inject(FormBuilder);
  protected widgetService = inject(WidgetService<CheckBoxCustomization>);
  protected uniqueIdService = inject(UniqueIdService);

  protected customizationForm = this.fb.group({
    id: [
      '',
      [
        Validators.required,
        this.uniqueIdService.forbiddenIdValidator(
          this.widgetService.uniqueId ?? '',
        ),
        this.uniqueIdService.isEmptySpace(),
      ],
    ],
    description: [''],
    defaultValue: [false],
    required: [false],
    weight: [0],
    showIfRule: ['', [this.uniqueIdService.idNotExistsValidator()]],
    autocompleteRule: ['', [this.uniqueIdService.idNotExistsValidator()]],
  });

  constructor() {
    effect(() => {
      const widget = this.widgetService.widget();
      if (!widget) return;

      const uniqueId = this.widgetService.uniqueId;
      if (!uniqueId) return;

      this.customizationForm.patchValue({
        id: uniqueId,
        description: widget.customization.description,
        defaultValue: widget.customization.defaultValue,
        required: widget.customization.required,
        weight: widget.customization.weight,
        showIfRule: widget.customization.showIfRule,
        autocompleteRule: widget.customization.autocompleteRule,
      });
    });
  }

  updateWidget($event: boolean) {
    if (!$event) {
      this.widgetService.settingVisibility.set(false);
      return;
    }

    const formValue = this.customizationForm.value;
    const id = formValue.id?.trim();
    if (!id) return;
    this.widgetService.updateWidget(id, {
      description: formValue.description,
      defaultValue: formValue.defaultValue,
      required: formValue.required,
      weight: formValue.weight,
      showIfRule: formValue.showIfRule,
      autocompleteRule: formValue.autocompleteRule,
    });
    this.widgetService.settingVisibility.set(false);
  }
}
